/**
 * Web Location permission hook.
 *
 * The browser exposes permission differently from native, so this is a separate
 * file rather than a branch:
 *
 *  - Status comes from `navigator.permissions.query({ name: 'geolocation' })`,
 *    which reports `prompt` (never asked) as `undetermined`. A browser without
 *    the Permissions API is `undetermined`, never `denied`.
 *  - `request()` can only surface a prompt while undetermined; a prior denial
 *    cannot be re-prompted from script, and there is no settings deep-link. Hence
 *    `canOpenSettings` is false and denied users get `deniedGuidance` instead.
 *
 * @module features/settings/hooks/use-permission.location
 */

import { useCallback, useEffect, useState } from 'react';
import type {
  LocationPermissionStatus,
  UseLocationPermissionResult,
} from './use-permission.location.types';

export const LOCATION_DENIED_GUIDANCE =
  'Enable location in your browser: tap the lock icon in the address bar and allow Location.';

function toStatus(state: PermissionState): LocationPermissionStatus {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'undetermined';
}

/**
 * Reads the current permission without prompting. A browser that cannot answer
 * the query reports `undetermined` — the caller would otherwise tell the user
 * they refused access they were never asked for.
 */
async function queryStatus(): Promise<LocationPermissionStatus> {
  try {
    const result = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    return toStatus(result.state);
  } catch {
    return 'undetermined';
  }
}

export function useLocationPermissionSetting(): UseLocationPermissionResult {
  const [status, setStatus] = useState<LocationPermissionStatus>('undetermined');

  useEffect(() => {
    let active = true;
    void queryStatus().then((next) => {
      if (active) setStatus(next);
    });
    return () => {
      active = false;
    };
  }, []);

  const request = useCallback(async () => {
    await new Promise<void>((resolve) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        resolve();
        return;
      }
      // Either outcome resolves: the goal is to trigger the browser prompt, then
      // re-read the authoritative status below.
      navigator.geolocation.getCurrentPosition(
        () => resolve(),
        () => resolve(),
      );
    });
    setStatus(await queryStatus());
  }, []);

  // No browser equivalent of a settings deep-link; the row shows guidance instead.
  const openSettings = useCallback(async () => {}, []);

  return {
    status,
    request,
    openSettings,
    canOpenSettings: false,
    deniedGuidance: LOCATION_DENIED_GUIDANCE,
  };
}
